import React from 'react';

import { Badge, Button, Table, TablePropertyFiltering, TableSorting, TablePagination } from 'AWS-UI-Components-React';

import { downloadFile, toDownloadFormat } from '../../../utilities';
import { TokenGroupDisplay } from '../../common/token_group_display';
import { V2_PROPERTY_FILTERING_TEXT, capFilterValues, v2PropertyFilteringFunction, getFilteredItems } from '../../common/property_filter_helpers';

const tablePageSize = 75;

// Each array's elements must equal the filter option values offered for its property.
const ARRAY_FIELD_BY_PROPERTY_KEY = { _productLinesText: '_productLineTokens' };
const filterProductGroup = (item, tokens, operation) =>
  v2PropertyFilteringFunction(item, tokens, operation, ARRAY_FIELD_BY_PROPERTY_KEY);

export class GLPLMappingTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = { filteringTokens: [], filteringOperation: 'and' };
  }

  getColumnDefinitions = (productGroupIdToProductLines) => {
    return [
      {
        id: 'id',
        header: 'Id',
        minWidth: '150px',
        cell: (item) => {
          return (
            <span>
              {item.id} {this.getBadges(item, productGroupIdToProductLines)}
            </span>
          );
        },
      },
      {
        id: 'name',
        header: 'Name',
        minWidth: '250px',
        cell: (item) => item.name,
      },
      {
        id: 'productLines',
        header: 'Product Lines',
        cell: (item) => this.productLinesDisplay(item, productGroupIdToProductLines),
      },
    ];
  };

  getBadges = (productGroup, productGroupIdToProductLines) => {
    return !productGroupIdToProductLines[productGroup.id] ? (
      <span key={productGroup.id}>
        <Badge color={'grey'} content={'Unmapped'} />
      </span>
    ) : null;
  };

  productLinesDisplay = (productGroup, productGroupIdToProductLines) => {
    const productLines = productGroupIdToProductLines[productGroup.id];
    const productLineDisplayItems = (productLines || []).map((productLine) => {
      return {
        label: `${productLine.name} (${productLine.id})`,
      };
    });

    return (
      <div className="awsui-grid">
        <div className="awsui-util-pt-m awsui-util-mb-s">
         <div className="awsui-row">
            <TokenGroupDisplay items={productLineDisplayItems} />
         </div>
        </div>
      </div>
    );
  };

  productGroupIdToProductLines = (productGroupsById, productLinesById) => {
    let productGroupIdToProductLines = {};

    for (let id of Object.keys(productLinesById)) {
      const productLine = productLinesById[id];
      const gls = productLine.gls || [];
      for (let gl of gls) {
        const existingMapping = productGroupIdToProductLines[gl];
        const productGroup = productGroupsById[gl];
        if (productGroup) {
          productGroupIdToProductLines[gl] = (existingMapping ? existingMapping : []).concat(productLine);
        }
      }
    }

    return productGroupIdToProductLines;
  };

  getFilteringOptions = (productGroupItems) => {
    const idOptions = new Set();
    const nameOptions = new Set();
    const productLineOptions = new Set();
    (productGroupItems || []).forEach((productGroup) => {
      if (productGroup.id != null) idOptions.add(`${productGroup.id}`);
      if (productGroup.name) nameOptions.add(productGroup.name);
      if (Array.isArray(productGroup._productLineTokens)) {
        productGroup._productLineTokens.forEach((productLine) => productLine && productLineOptions.add(productLine));
      }
    });
    return [
      { groupValuesLabel: 'Id Values', propertyKey: '_idText', propertyLabel: 'Id', values: capFilterValues(Array.from(idOptions)) },
      { groupValuesLabel: 'Name Values', propertyKey: 'name', propertyLabel: 'Name', values: capFilterValues(Array.from(nameOptions)) },
      { groupValuesLabel: 'Product Lines Values', propertyKey: '_productLinesText', propertyLabel: 'Product Lines', values: capFilterValues(Array.from(productLineOptions)) },
      // Mirrors the grey 'Unmapped' badge so users can isolate mapped vs unmapped groups.
      { groupValuesLabel: 'Mapping status Values', propertyKey: '_mappingStatus', propertyLabel: 'Mapping status', values: ['Single mapped', 'Unmapped'] },
    ];
  };

  // Keyed on the props maps, not `productGroupItems` (rebuilt every render); sound only
  // because GLPLMapperContainer swaps in new maps on each pull and never mutates them.
  _filteringOptionsMemo = { groupsKey: undefined, linesKey: undefined, value: undefined };

  getMemoizedFilteringOptions = (productGroupItems, productGroupsById, productLinesById) => {
    if (this._filteringOptionsMemo.groupsKey !== productGroupsById || this._filteringOptionsMemo.linesKey !== productLinesById) {
      this._filteringOptionsMemo = {
        groupsKey: productGroupsById,
        linesKey: productLinesById,
        value: this.getFilteringOptions(productGroupItems),
      };
    }
    return this._filteringOptionsMemo.value;
  };

  onClickDownloadData = (columnDefinitions, productGroupItems, productGroupIdToProductLines) => {
    const { filteringTokens, filteringOperation } = this.state;
    const productGroupsWithProductLines = getFilteredItems(productGroupItems, filteringTokens, filteringOperation, ARRAY_FIELD_BY_PROPERTY_KEY).map((productGroup) => {
      const productLines = (productGroupIdToProductLines[productGroup.id] || [])
        .map((productLine) => `${productLine.name}(${productLine.id})`)
        .join(',');

      return {
        ...productGroup,
        productLines,
      };
    });
    const downloadColumnDefinitions = [...columnDefinitions, { id: '_mappingStatus', header: 'Mapping Status' }];
    const data = toDownloadFormat(downloadColumnDefinitions, productGroupsWithProductLines);
    downloadFile(data, 'ProductGroups.xls');
  };

  render() {
    const { productGroupsById, productLinesById } = this.props;
    const productGroupIdToProductLines = this.productGroupIdToProductLines(productGroupsById, productLinesById);
    const productGroups = Object.values(productGroupsById);
    const columnDefinitions = this.getColumnDefinitions(productGroupIdToProductLines);

    const productGroupItems = productGroups.map((productGroup) => {
      const mappedProductLines = productGroupIdToProductLines[productGroup.id];
      // Per-product-line display tokens ("name (id)"). Kept as a raw array so the
      // filter-option builder enumerates each token directly instead of splitting
      // the joined _productLinesText back apart -- a product-line name containing
      // ", " would otherwise corrupt that round-trip. _productLinesText stays the
      // joined, substring-searchable display field the property filter matches on.
      const productLineTokens = (mappedProductLines || []).map(
        (productLine) => `${productLine.name} (${productLine.id})`
      );
      return {
        ...productGroup,
        // productGroup.id is a NUMBER; the v2 partial-match predicate only matches string
        // fields, so Id filters on this coerced copy while the column still shows the number.
        _idText: String(productGroup.id),
        _productLineTokens: productLineTokens,
        _productLinesText: productLineTokens.join(', '),
        _mappingStatus: mappedProductLines ? 'Single mapped' : 'Unmapped',
      };
    });

    return (
      <div id="gl-pl-mapping-table">
        <Table
          columnDefinitions={columnDefinitions}
          items={productGroupItems}
          header={
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <h2>Product Groups</h2>
              </div>
              <div className="awsui-util-action-stripe-group">
                <Button
                  text="Download Data"
                  onClick={() =>
                    this.onClickDownloadData(columnDefinitions, productGroupItems, productGroupIdToProductLines)
                  }
                  icon="download"
                />
              </div>
            </div>
          }
          loading={!productGroups}
          loadingText={'Loading product groups...'}
          stickyHeader
        >
          <TablePropertyFiltering
            {...V2_PROPERTY_FILTERING_TEXT}
            filteringOptions={this.getMemoizedFilteringOptions(productGroupItems, productGroupsById, productLinesById)}
            filteringFunction={filterProductGroup}
            placeholder="Filter product groups"
            tokens={this.state.filteringTokens}
            operation={this.state.filteringOperation}
            onPropertyFilteringChange={(event) =>
              this.setState({ filteringTokens: event.detail.tokens, filteringOperation: event.detail.operation })
            }
          />
          <TableSorting
            sortingColumn="id"
            sortableColumns={[
              { id: 'id', field: 'id' },
              { id: 'name', field: 'name' },
              { id: 'productLines', field: 'productLines' },
            ]}
          />
          <TablePagination pageSize={tablePageSize} />
        </Table>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/gl_pl_mapping/gl_pl_mapping_table.jsx